// Copyright (c) 2023-present VexFlow contributors: https://github.com/vexflow/vexflow/graphs/contributors
//
// ## Description
// `StemmableNote` is an abstract interface for notes with optional stems.
// Examples of stemmable notes are `StaveNote` and `TabNote`
import { Element } from './element';
import { Glyphs } from './glyphs';
import { Note } from './note';
import { Stem } from './stem';
import { Tables } from './tables';
import { Category } from './typeguard';
import { RuntimeError } from './util';
export class StemmableNote extends Note {
    static get CATEGORY() {
        return Category.StemmableNote;
    }
    constructor(noteStruct) {
        super(noteStruct);
        this.flag = new Element();
        this.flagStyle = {};
    }
    // Get and set the note's `Stem`
    getStem() {
        return this.stem;
    }
    checkStem() {
        if (!this.stem) {
            throw new RuntimeError('NoStem', 'No stem attached to instance');
        }
        return this.stem;
    }
    setStem(stem) {
        this.stem = stem;
        this.addChildElement(stem);
        return this;
    }
    // Builds and sets a new stem
    buildStem() {
        const stem = new Stem();
        this.setStem(stem);
        return this;
    }
    buildFlag() {
        var _a, _b, _c;
        const { glyphProps } = this;
        if (this.hasFlag()) {
            const flagCode = 
            // codeFlagDown = codeFlagUp + 1
            // if codeFlagUp is undefined, codePoint will be 0
            this.getStemDirection() === Stem.DOWN
                ? String.fromCodePoint(((_b = (_a = glyphProps.codeFlagUp) === null || _a === void 0 ? void 0 : _a.codePointAt(0)) !== null && _b !== void 0 ? _b : -1) + 1)
                : (_c = glyphProps.codeFlagUp) !== null && _c !== void 0 ? _c : Glyphs.null;
            this.flag.setText(flagCode);
            this.flag.fontInfo = this.fontInfo;
        }
    }
    // Get the custom glyph associated with the outer note head on the base of the stem.
    getBaseCustomNoteHeadGlyphProps() {
        if (this.getStemDirection() === Stem.DOWN) {
            return this.customGlyphs[this.customGlyphs.length - 1];
        }
        else {
            return this.customGlyphs[0];
        }
    }
    // Get the full length of stem
    getStemLength() {
        return Stem.HEIGHT + this.getStemExtension();
    }
    // Get the number of beams for this duration
    getBeamCount() {
        const glyphProps = this.getGlyphProps();
        if (glyphProps) {
            return glyphProps.beamCount;
        }
        else {
            return 0;
        }
    }
    // Get the minimum length of stem
    getStemMinimumLength() {
        const frac = Tables.durationToFraction(this.duration);
        const beamIsUndefined = this.beam === undefined;
        let length = frac.value() <= 1 ? 0 : 20;
        // if note is flagged, cannot shorten beam
        switch (this.duration) {
            case '8':
            case '16':
                length = beamIsUndefined ? 35 : 25;
                break;
            case '32':
                length = beamIsUndefined ? 45 : 35;
                break;
            case '64':
                length = beamIsUndefined ? 50 : 40;
                break;
            case '128':
                length = beamIsUndefined ? 55 : 45;
                break;
            default:
                break;
        }
        return length;
    }
    // Get/set the direction of the stem
    getStemDirection() {
        if (!this.stemDirection)
            throw new RuntimeError('NoStem', 'No stem attached to this note.');
        return this.stemDirection;
    }
    setStemDirection(direction) {
        if (!direction)
            direction = Stem.UP;
        if (direction !== Stem.UP && direction !== Stem.DOWN) {
            throw new RuntimeError('BadArgument', `Invalid stem direction: ${direction}`);
        }
        this.stemDirection = direction;
        // Reset and reformat everything. Flag has to be built before calling getStemExtension.
        this.reset();
        if (this.hasFlag()) {
            this.buildFlag();
        }
        this.beam = undefined;
        if (this.stem) {
            this.stem.setDirection(direction);
            this.stem.setExtension(this.getStemExtension());
        }
        if (this.preFormatted) {
            this.preFormat();
        }
        return this;
    }
    // Get the `x` coordinate of the stem
    getStemX() {
        const xBegin = this.getAbsoluteX() + this.xShift;
        const xEnd = this.getAbsoluteX() + this.xShift + this.getGlyphWidth();
        const stemX = this.stemDirection === Stem.DOWN ? xBegin : xEnd;
        return stemX;
    }
    // Get the `x` coordinate for the center of the glyph.
    // Used for `TabNote` stems and stemlets over rests
    getCenterGlyphX() {
        return this.getAbsoluteX() + this.xShift + this.getGlyphWidth() / 2;
    }
    // Get the stem extension for the current duration
    getStemExtension() {
        const glyphProps = this.getGlyphProps();
        const flagHeight = this.flag.getHeight();
        const scale = this.getFontScale();
        if (this.stemExtensionOverride !== undefined) {
            return this.stemExtensionOverride;
        }
        // Use stemBeamExtension with beams
        if (this.beam) {
            return glyphProps.stemBeamExtension * scale;
        }
        // If the flag is longer than the stem, extend the stem by the difference.
        return flagHeight > Stem.HEIGHT * scale ? flagHeight - Stem.HEIGHT * scale : 0;
    }
    // Set the stem length to a specific. Will override the default length.
    setStemLength(height) {
        this.stemExtensionOverride = height - Stem.HEIGHT;
        return this;
    }
    // Get the top and bottom `y` values of the stem.
    getStemExtents() {
        if (!this.stem)
            throw new RuntimeError('NoStem', 'No stem attached to this note.');
        return this.stem.getExtents();
    }
    /** Gets the `y` value for the top modifiers at a specific `textLine`. */
    getYForTopText(textLine) {
        const stave = this.checkStave();
        if (this.hasStem()) {
            const extents = this.getStemExtents();
            if (!extents)
                throw new RuntimeError('InvalidState', 'Stem does not have extents.');
            return Math.min(stave.getYForTopText(textLine), extents.topY - this.renderOptions.annotationSpacing * (textLine + 1));
        }
        else {
            return stave.getYForTopText(textLine);
        }
    }
    /** Gets the `y` value for the bottom modifiers at a specific `textLine`. */
    getYForBottomText(textLine) {
        const stave = this.checkStave();
        if (this.hasStem()) {
            const extents = this.getStemExtents();
            if (!extents)
                throw new RuntimeError('InvalidState', 'Stem does not have extents.');
            return Math.max(stave.getYForTopText(textLine), extents.baseY + this.renderOptions.annotationSpacing * textLine);
        }
        else {
            return stave.getYForBottomText(textLine);
        }
    }
    hasFlag() {
        return this.glyphProps.codeFlagUp !== undefined && !this.beam && !this.isRest();
    }
    /** Post formats the note. */
    postFormat() {
        var _a;
        (_a = this.beam) === null || _a === void 0 ? void 0 : _a.postFormat();
        this.postFormatted = true;
        return this;
    }
    /** Renders the stem onto the canvas. */
    drawStem(stemOptions) {
        var _a;
        this.checkContext();
        this.setRendered();
        this.setStem(new Stem(stemOptions));
        (_a = this.stem) === null || _a === void 0 ? void 0 : _a.setContext(this.getContext()).draw();
    }
}
